import { QueryCache, QueryClient } from '@tanstack/react-query';
import { useStore } from '../store';

/** The toast a query asks the shared error handler to show when its fetch fails. */
interface QueryErrorToast {
  title: string;
  description?: string;
}

function readErrorToast(
  meta: Record<string, unknown> | undefined
): QueryErrorToast | undefined {
  const toast = meta?.errorToast;
  if (typeof toast !== 'object' || toast === null) return undefined;
  const { title, description } = toast as Partial<QueryErrorToast>;
  return typeof title === 'string' ? { title, description } : undefined;
}

/**
 * The cache carrying the shared error handler.
 *
 * A factory rather than a module-level instance so tests can build a client
 * with the same handler but their own isolated cache — the toast behavior below
 * is only real if the tests exercise it rather than a bare `new QueryClient()`.
 */
export function createQueryCache(): QueryCache {
  return new QueryCache({
    /**
     * Where a failed query becomes a toast. Deliberately here and not in a
     * `useEffect` on `query.error`: the cache outlives the components observing
     * it, so an effect re-fires for the *same* cached failure every time the
     * user navigates back to the screen. This runs once per failed fetch.
     *
     * Queries opt in by setting `meta.errorToast`. Everything else only logs,
     * which is what the non-fatal loads (tab counts, payor options) want — they
     * degrade to '-' rather than interrupting the user.
     */
    onError: (error, query) => {
      console.error('Query failed', { queryKey: query.queryKey, error });
      const toast = readErrorToast(query.meta);
      if (!toast) return;
      useStore.getState().showError(toast.title, toast.description);
    },
  });
}

/**
 * The app-wide TanStack Query client.
 *
 * Scope note: server state is migrating to Query one surface at a time (Billing
 * Manager first). Zustand keeps everything Query is not for — auth, toasts, and
 * the persisted *UI* query state in `billingUiSlice` (filters, sort, pageSize).
 * The split is: Query owns what the server said, Zustand owns what the user
 * chose. Do not move UI state into Query, or server data into Zustand.
 */
export const queryClient = new QueryClient({
  queryCache: createQueryCache(),
  defaultOptions: {
    queries: {
      // Deliberately 0 as the app-wide default: a future query should opt into
      // a freshness policy rather than inherit one. Billing opts in via
      // BILLING_STALE_TIME_MS below.
      staleTime: 0,
      // Kept off to preserve today's behavior exactly: nothing in the app
      // currently refetches on focus, and turning it on app-wide inside a
      // refactor would change request volume on every screen at once. Worth
      // revisiting per-query once this has settled.
      refetchOnWindowFocus: false,
      // One retry, not the default three: a failing billing endpoint should
      // surface to the user quickly rather than after three backoffs.
      retry: 1,
    },
  },
});

/**
 * How long a Billing Manager list stays fresh before a mount revalidates it.
 *
 * With `staleTime: 0` every tab switch served the cached rows instantly and
 * then revalidated — normally invisible, but `/billing/billing-cycles/drafts`
 * measures 4–11s locally, so each switch parked a visible "Updating…" on data
 * that was already correct.
 *
 * This does not weaken the freshness the user can observe from their own
 * actions: mutations invalidate explicitly (`onCountsInvalidated`,
 * `mutateSelected`), and the page-level Refresh invalidates everything. The
 * only staleness window is another user changing billing data while this one
 * passively looks at it, which 30s bounds acceptably.
 *
 * Applied per-query rather than as a client default so nothing else in the app
 * silently inherits it.
 */
export const BILLING_STALE_TIME_MS = 30_000;

/**
 * Query keys for the Billing Manager.
 *
 * Centralized because invalidation happens far from the fetch: the page-level
 * Refresh button and post-mutation reloads live in `EOEClaimsManager`, while
 * the fetches live in `useBillingCycles` and `RemittanceManager`. Keys are
 * hierarchical, so `invalidateQueries({ queryKey: billingKeys.remittances() })`
 * matches every remittance list regardless of its filter params.
 */
export const billingKeys = {
  all: ['billing'] as const,

  /** Claims list, one entry per tab payload (status filter + toolbar state). */
  billingCycles: (payload?: unknown) =>
    payload === undefined
      ? ([...billingKeys.all, 'cycles'] as const)
      : ([...billingKeys.all, 'cycles', payload] as const),

  /** Tab counts + insurance/clinic option lists for the claims view. */
  billingMeta: (filters?: {
    hideTestPatients: boolean;
    showInactiveEpisodes: boolean;
    showNothingToBill: boolean;
    hideImportedPatients: boolean;
    excludePreTakeover: boolean;
  }) =>
    filters === undefined
      ? ([...billingKeys.all, 'meta'] as const)
      : ([...billingKeys.all, 'meta', filters] as const),

  /** Remittance list, one entry per toolbar/sort/page params object. */
  remittances: (params?: unknown) =>
    params === undefined
      ? ([...billingKeys.all, 'remittances'] as const)
      : ([...billingKeys.all, 'remittances', params] as const),

  /** Payor filter options for the remittance toolbar. */
  remittancePayors: () => [...billingKeys.all, 'remittance-payors'] as const,

  /** Status-tab counts for the remittance view, keyed by the hide-test-patients
   *  population default (the counts honor it; view filters never touch them). */
  remittanceStatusCounts: (hideTestPatients?: boolean) =>
    hideTestPatients === undefined
      ? ([...billingKeys.all, 'remittance-status-counts'] as const)
      : ([
          ...billingKeys.all,
          'remittance-status-counts',
          { hideTestPatients },
        ] as const),

  /** A single remittance's detail payload, keyed by id. */
  remittanceDetail: (id: string) =>
    [...billingKeys.all, 'remittance-detail', id] as const,
};

/**
 * How long a clinician-workload week stays fresh.
 *
 * The number moves whenever anyone assigns work, so it cannot be cached for
 * long — but it does not move second to second, and reopening the same day's
 * dropdown should not send a second request. Every entry is keyed by day, so
 * this buys repeat visits to one day rather than a whole week's rows; a minute
 * is short enough that a colleague's assignment surfaces almost at once.
 *
 * The scheduler's *own* assignments do not wait for this: they invalidate
 * explicitly, because a number that ignores what you just did is worse than no
 * number at all.
 */
export const SCHEDULING_POINT_LOAD_STALE_TIME_MS = 60_000;

/**
 * Query keys for Scheduling Points.
 *
 * Hierarchical for the same reason as `billingKeys`: invalidation happens at
 * the assignment site, far from the dropdown that fetched it, and
 * `invalidateQueries({ queryKey: schedulingPointKeys.loads() })` reaches every
 * clinic/week entry without the caller having to know which weeks are cached.
 */
export const schedulingPointKeys = {
  all: ['scheduling-points'] as const,

  /** Every cached workload week, across all clinics. */
  loads: () => [...schedulingPointKeys.all, 'load'] as const,

  /**
   * One clinic's workload as of one date.
   *
   * Keyed on the date being scheduled, not on its week. The response is
   * week-wide except for the figures describing that day, so collapsing a week
   * onto one entry made every "this task's day" figure describe Monday — see
   * `useSchedulingPointLoads`. Two tasks on the same day still share one fetch.
   */
  load: (clinicId: string, dateISO: string) =>
    [...schedulingPointKeys.loads(), clinicId, dateISO] as const,
};
